"use client"

import Link from "next/link"
import { useEffect, useMemo, useState } from "react"
import { Navbar } from "@/components/navbar"
import { Footer } from "@/components/footer"
import { apiStore } from "@/lib/api-store"
import type { Product } from "@/lib/types"
import { formatPrice } from "@/lib/utils"
import {
  ArrowRight,
  Clock,
  Drum,
  Guitar,
  Lightbulb,
  Mail,
  MapPin,
  Music,
  Package,
  Phone,
  Quote,
  Radio,
  Sparkles,
  Star,
  Tag,
  Zap,
} from "lucide-react"

// SVG inline pour les icônes sociales (lucide a déprécié les icônes de marques)
const IconInstagram = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
)
const IconFacebook = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
)
const IconX = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden>
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
  </svg>
)
const IconYoutube = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
)
const IconLinkedin = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
)

// ─── Static data ─────────────────────────────────────────────────────────────

const COMPANY_GALLERY_IMAGES = [
  "/wireless-headphones.png",
  "/promotion.jpg",
  "/offre_speciale.jpg",
  "/placeholder.jpg",
  "/diverse-products-still-life.png",
  "/logo_sw.png",
  "/placeholder-logo.png",
]

const TOP_DISPLAY_COUNT = 3

const PROMO_END = new Date("2026-05-01T23:59:59")

const CATEGORIES = [
  { icon: Guitar, label: "Instruments à Cordes", desc: "Guitares, basses, violons, pianos", image: "/productcategory/Stringed_Instruments.jpg" },
  { icon: Music, label: "Instruments à Vent", desc: "Saxophones, trompettes, flûtes", image: "/productcategory/instruments2.jpg" },
  { icon: Drum, label: "Percussions", desc: "Batteries, djembés, congas", image: "/productcategory/10560970-percussions-et-bruit-instruments-de-musique-vectoriel.jpg" },
  { icon: Radio, label: "Sonorisation", desc: "Enceintes, amplis, micros pro", image: "/productcategory/modularite.jpg" },
  { icon: Lightbulb, label: "Éclairage Scénique", desc: "Projecteurs, jeux de lumière, DMX", image: "/productcategory/TK016-1.png" },
  { icon: Package, label: "Accessoires", desc: "Câbles, housses, pieds, cordes", image: "/productcategory/preview.jpg" },
]

const TESTIMONIALS = [
  {
    name: "Mamadou K.",
    role: "Guitariste professionnel",
    rating: 5,
    text: "Excellente boutique — j'ai trouvé exactement la guitare que je cherchais. Le conseil de l'équipe est vraiment précieux.",
    avatar: "MK",
    color: "bg-blue-500",
  },
  {
    name: "Fatou D.",
    role: "Chanteuse & compositrice",
    rating: 5,
    text: "Service impeccable, livraison rapide et produits authentiques. Je recommande vivement à tous les musiciens de la ville.",
    avatar: "FD",
    color: "bg-pink-500",
  },
  {
    name: "Ibrahim S.",
    role: "Ingénieur du son",
    rating: 5,
    text: "Meilleur choix de matériel de sonorisation à Ouagadougou. Prix compétitifs et SAV vraiment réactif.",
    avatar: "IS",
    color: "bg-green-500",
  },
  {
    name: "Aïcha B.",
    role: "Professeur de musique",
    rating: 4,
    text: "Large gamme d'instruments pédagogiques. Mon école de musique s'approvisionne exclusivement ici depuis 2 ans.",
    avatar: "AB",
    color: "bg-purple-500",
  },
]

const PARTNERS = [
  { name: "YAMAHA", category: "Instruments & Audio", color: "#1a1a1a", bg: "" },
  { name: "ROLAND", category: "Claviers & Studio", color: "#c0392b", bg: "" },
  { name: "SHURE", category: "Microphones", color: "#003087", bg: "" },
  { name: "PIONEER DJ", category: "DJ & Club", color: "#1a1a1a", bg: "" },
  { name: "FENDER", category: "Guitares", color: "#cc2200", bg: "" },
  { name: "MARSHALL", category: "Amplificateurs", color: "#f5c518", bg: "#1a1a1a" },
  { name: "BEHRINGER", category: "Audio Pro", color: "#0055a4", bg: "" },
  { name: "SENNHEISER", category: "Casques & Micros", color: "#1a1a1a", bg: "" },
  { name: "JBL", category: "Enceintes", color: "#ffffff", bg: "#f97316" },
  { name: "CASIO", category: "Claviers", color: "#005bac", bg: "" },
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getProductCoverImage(product?: Product | null): string | null {
  const first = product?.images?.find((img) => typeof img === "string" && img.trim().length > 0)
  return first || null
}

function isExternalUrl(url: string): boolean {
  return /^https?:\/\//i.test(url)
}

function pickRandomItems<T>(items: T[], count: number): T[] {
  const safeCount = Math.max(0, Math.min(count, items.length))
  const copy = [...items]
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
      ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy.slice(0, safeCount)
}

function productKey(product: Product): string {
  return product._id || product.id || product.name
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function Home() {
  const companyName = (process.env.NEXT_PUBLIC_COMPANY_NAME || "Notre boutique musique").toUpperCase()

  const socialLinks = [
    { label: "Instagram", href: process.env.NEXT_PUBLIC_SOCIAL_INSTAGRAM || "#", Icon: IconInstagram },
    { label: "Facebook", href: process.env.NEXT_PUBLIC_SOCIAL_FACEBOOK || "#", Icon: IconFacebook },
    { label: "X", href: process.env.NEXT_PUBLIC_SOCIAL_X || "#", Icon: IconX },
    { label: "YouTube", href: process.env.NEXT_PUBLIC_SOCIAL_YOUTUBE || "#", Icon: IconYoutube },
    { label: "LinkedIn", href: process.env.NEXT_PUBLIC_SOCIAL_LINKEDIN || "#", Icon: IconLinkedin },
  ]

  const contactInfo = [
    { icon: Mail, title: "Email", value: process.env.NEXT_PUBLIC_COMPANY_EMAIL || "barakhelmusic@gmail.com", description: "Réponse dans les 24h", color: "bg-blue-500" },
    { icon: Phone, title: "Téléphone", value: process.env.NEXT_PUBLIC_COMPANY_PHONE || "+226 61 74 22 74", description: "Lundi - Samedi, 8h-19h", color: "bg-green-500" },
    { icon: MapPin, title: "Adresse", value: process.env.NEXT_PUBLIC_COMPANY_ADDRESS || "Samandin, Ouagadougou, Burkina Faso", description: "Boutique principale", color: "bg-orange-500" },
    { icon: Clock, title: "Horaires", value: "Lun-Sam: 8h-19h", description: "Dimanche fermé", color: "bg-purple-500" },
  ]

  // ── State ──────────────────────────────────────────────────────────────────

  const [topPool, setTopPool] = useState<Product[]>([])
  const [topDisplayed, setTopDisplayed] = useState<Product[]>([])
  const [nouveautes, setNouveautes] = useState<Product[]>([])
  const [galleryImages, setGalleryImages] = useState<string[]>([])
  const [loadingTop, setLoadingTop] = useState(true)
  const [loadingNouveautes, setLoadingNouveautes] = useState(true)
  const [countdown, setCountdown] = useState({ d: 0, h: 0, m: 0, s: 0 })

  const topInfo = useMemo(() => (topPool.length === 0 ? "Sélection du moment" : "Nos best-sellers"), [topPool.length])
  const topDisplayed3 = useMemo(() => topDisplayed.slice(0, TOP_DISPLAY_COUNT), [topDisplayed])

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    setGalleryImages(pickRandomItems(COMPANY_GALLERY_IMAGES, 5))
  }, [])

  // countdown
  useEffect(() => {
    const tick = () => {
      const diff = PROMO_END.getTime() - Date.now()
      if (diff <= 0) { setCountdown({ d: 0, h: 0, m: 0, s: 0 }); return }
      const d = Math.floor(diff / 86_400_000)
      const h = Math.floor((diff % 86_400_000) / 3_600_000)
      const m = Math.floor((diff % 3_600_000) / 60_000)
      const s = Math.floor((diff % 60_000) / 1_000)
      setCountdown({ d, h, m, s })
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [])

  // top products
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoadingTop(true)
      try {
        const [bySales, byViews] = await Promise.all([
          apiStore.getProducts({ sortBy: "sales", limit: 20 }),
          apiStore.getProducts({ sortBy: "views", limit: 20 }),
        ])
        const map = new Map<string, Product>()
          ;[...(bySales || []), ...(byViews || [])].forEach((p) => { map.set(productKey(p), p) })
        const merged = Array.from(map.values()).filter((p) => !!getProductCoverImage(p))
        if (!cancelled) { setTopPool(merged); setTopDisplayed(pickRandomItems(merged, TOP_DISPLAY_COUNT)) }
      } catch (e) {
        console.error("Erreur top:", e)
        if (!cancelled) { setTopPool([]); setTopDisplayed([]) }
      } finally {
        if (!cancelled) setLoadingTop(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  // nouveautés
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoadingNouveautes(true)
      try {
        const products = await apiStore.getProducts({ sortBy: "createdAt", limit: 6 })
        const withImages = (products || []).filter((p) => !!getProductCoverImage(p))
        if (!cancelled) setNouveautes(withImages.slice(0, 6))
      } catch (e) {
        console.error("Erreur nouveautés:", e)
        if (!cancelled) setNouveautes([])
      } finally {
        if (!cancelled) setLoadingNouveautes(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (topPool.length === 0) return
    const interval = setInterval(() => {
      setTopDisplayed(pickRandomItems(topPool, TOP_DISPLAY_COUNT))
    }, 5000)
    return () => clearInterval(interval)
  }, [topPool])

  // ── JSX ────────────────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />

      <main className="flex-1">

        {/* ── HERO ──────────────────────────────────────────────────── */}
        <section className="relative flex min-h-[560px] items-center overflow-hidden lg:min-h-[640px]">

          {/* fond sombre côté gauche (toujours visible) */}
          <div className="absolute inset-0" style={{ background: "#080f1c" }} />

          {/* image positionnée sur la moitié droite */}
          <div className="absolute right-0 top-0 h-full w-full lg:w-[58%]">
            <img
              src="/covernew.jpg"
              alt="Instruments de musique"
              className="h-full w-full object-cover object-center"
              loading="eager"
            />
            {/* fondu gauche : raccord avec le fond sombre */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to right, #080f1c 0%, rgba(8,15,28,0.85) 18%, rgba(8,15,28,0.45) 45%, rgba(8,15,28,0.15) 70%, transparent 100%)",
              }}
            />
            {/* léger assombrissement global pour lisibilité */}
            <div className="absolute inset-0 bg-black/20" />
          </div>

          {/* grille subtile sur toute la section */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
          />

          {/* contenu texte — colonne gauche */}
          <div className="container relative py-16 lg:py-24">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/8 px-4 py-1.5 text-xs font-semibold text-white/70 backdrop-blur-sm">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-400" />
                {companyName}
              </span>

              <h1 className="mt-6 text-5xl font-extrabold leading-[1.1] tracking-tight text-white md:text-6xl lg:text-7xl">
                Tout pour
                <br />
                <span
                  style={{
                    background: "linear-gradient(90deg, #60a5fa 0%, #a78bfa 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  votre son
                </span>
              </h1>

              <p className="mt-5 max-w-md text-lg leading-relaxed text-white/55">
                Du studio à la scène — instruments, home‑studio, sonorisation et accessoires pour chaque musicien.
              </p>

              <div className="mt-9 flex flex-wrap gap-3">
                <Link
                  href="/boutique"
                  className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-white shadow-lg transition-all hover:scale-105 hover:shadow-xl"
                  style={{ background: "linear-gradient(135deg, #2f5d97 0%, #3b74b5 100%)" }}
                >
                  Voir la boutique
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  href="/contact"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/8 px-7 py-3.5 text-sm font-bold text-white backdrop-blur-sm transition-all hover:bg-white/15"
                >
                  Nous contacter
                </Link>
              </div>

              <div className="mt-10 flex items-center gap-8 border-t border-white/10 pt-8">
                {[
                  { value: "500+", label: "Produits" },
                  { value: "50+", label: "Marques" },
                  { value: "24h", label: "Réponse SAV" },
                ].map((s) => (
                  <div key={s.label}>
                    <p className="text-2xl font-extrabold text-white">{s.value}</p>
                    <p className="mt-0.5 text-xs font-medium text-white/45">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── NOS PARTENAIRES ───────────────────────────────────────── */}
        <section className="bg-background">
          <div className="container py-12">
            <div className="text-center">
              <span className="inline-block rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold text-primary">
                Partenaires
              </span>
              <h2 className="mt-3 text-2xl font-extrabold tracking-tight text-foreground">
                Nos marques partenaires
              </h2>
              <p className="mt-2 text-sm text-foreground-secondary">
                Nous travaillons avec les références mondiales de l'industrie musicale.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
              {PARTNERS.map((p) => (
                <div
                  key={p.name}
                  className="group flex flex-col items-center justify-center gap-3 rounded-2xl border border-border bg-background px-5 py-6 text-center transition-all duration-200 hover:-translate-y-1 hover:border-primary/30 hover:bg-primary/5 hover:shadow-md"
                >
                  <div
                    className="flex h-10 w-full items-center justify-center rounded-lg px-3 transition-opacity duration-200 opacity-60 group-hover:opacity-100"
                    style={{ background: p.bg || "transparent" }}
                  >
                    <span
                      className="text-base font-black tracking-tight leading-none"
                      style={{ color: p.color, fontFamily: "'Arial Black', Arial, sans-serif" }}
                    >
                      {p.name}
                    </span>
                  </div>
                  <p className="text-[10px] font-medium text-foreground-secondary">{p.category}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CATEGORIES ────────────────────────────────────────────── */}
        <section className="bg-background-secondary">
          <div className="container py-16">
            <div className="mx-auto max-w-2xl text-center">
              <span className="inline-block rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold text-primary">Catalogue</span>
              <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
                Nos catégories de produits
              </h2>
              <p className="mt-3 text-base text-foreground-secondary">
                Du débutant au professionnel, trouvez l'équipement qui correspond à votre pratique.
              </p>
            </div>

            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon
                return (
                  <Link
                    key={cat.label}
                    href="/boutique"
                    className="group relative h-52 overflow-hidden rounded-2xl shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
                  >
                    {/* image de fond */}
                    <img
                      src={cat.image}
                      alt={cat.label}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    {/* overlay dégradé permanent */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
                    {/* contenu */}
                    <div className="absolute inset-0 flex flex-col justify-end p-5">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                          <Icon className="h-4 w-4 text-white" />
                        </span>
                        <h3 className="text-base font-bold text-white">{cat.label}</h3>
                      </div>
                      <p className="mt-1.5 text-sm text-white/65">{cat.desc}</p>
                      <div className="mt-3 flex h-5 items-center gap-1 overflow-hidden text-xs font-semibold text-white/80">
                        <span className="translate-y-5 transition-transform duration-200 group-hover:translate-y-0">
                          Voir les produits
                        </span>
                        <ArrowRight size={12} className="translate-y-5 transition-transform duration-200 group-hover:translate-y-0" />
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>
        </section>

        {/* ── NOUVEAUTÉS ────────────────────────────────────────────── */}
        <section className="bg-background">
          <div className="container py-16">
            <div className="flex items-end justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold text-primary">
                  <Sparkles size={11} />
                  Arrivages
                </span>
                <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-foreground">
                  Nouveautés
                </h2>
                <p className="mt-2 text-foreground-secondary">Les derniers produits ajoutés à notre catalogue.</p>
              </div>
              <Link
                href="/boutique"
                className="hidden shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition hover:underline sm:flex"
              >
                Tout voir <ArrowRight size={14} />
              </Link>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
              {(loadingNouveautes ? Array.from({ length: 6 }) : nouveautes).map((item, idx) => {
                if (loadingNouveautes || !item) {
                  return (
                    <div key={`nv-sk-${idx}`} className="overflow-hidden rounded-2xl border border-border bg-background shadow-sm">
                      <div className="h-40 w-full animate-pulse bg-background-secondary" />
                      <div className="space-y-1.5 p-3">
                        <div className="h-3.5 w-3/4 animate-pulse rounded bg-background-secondary" />
                        <div className="h-3 w-1/2 animate-pulse rounded bg-background-secondary" />
                      </div>
                    </div>
                  )
                }

                const product = item as Product
                const href = product._id ? `/produits/${product._id}` : "/boutique"
                const src = getProductCoverImage(product) || "/placeholder.svg"

                return (
                  <Link
                    key={productKey(product)}
                    href={href}
                    className="group overflow-hidden rounded-2xl border border-border bg-background shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
                  >
                    <div className="relative h-40 w-full overflow-hidden bg-background-secondary">
                      <img
                        src={src}
                        alt={product.name}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute left-2 top-2 rounded-full bg-primary px-2 py-0.5 text-[9px] font-bold text-white">
                        Nouveau
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="line-clamp-1 text-xs font-semibold text-foreground">{product.name}</p>
                      <p className="mt-1 text-xs font-extrabold text-primary">{formatPrice(product.price)}</p>
                    </div>
                  </Link>
                )
              })}
            </div>

            <div className="mt-8 text-center sm:hidden">
              <Link
                href="/boutique"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition hover:underline"
              >
                Tout voir <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>

        {/* ── BEST SELLERS ──────────────────────────────────────────── */}
        <section className="bg-background-secondary">
          <div className="container py-16">
            <div className="grid gap-10 lg:grid-cols-12">
              <div className="flex flex-col justify-center lg:col-span-4">
                <span className="inline-block w-fit rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold text-primary">Best-sellers</span>
                <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground">Les indispensables du moment</h2>
                <p className="mt-3 text-foreground-secondary">{topInfo}. Des équipements plébiscités pour leur fiabilité et leur qualité sonore.</p>
                <div className="mt-8">
                  <Link
                    href="/boutique"
                    className="group inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-white transition-all hover:bg-primary/90 hover:shadow-lg"
                  >
                    Voir tout
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
                <div className="mt-8 flex items-center gap-1.5">
                  <span className="h-2 w-7 rounded-full bg-primary" />
                  <span className="h-2 w-2 rounded-full bg-primary/30" />
                  <span className="h-2 w-2 rounded-full bg-primary/30" />
                </div>
              </div>

              <div className="lg:col-span-8">
                <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3">
                  {(loadingTop ? [0, 1, 2] : topDisplayed3).map((item, idx) => {
                    if (loadingTop) {
                      return (
                        <div key={`sk-${idx}`} className="overflow-hidden rounded-2xl border border-border bg-background shadow-sm">
                          <div className="h-52 w-full animate-pulse bg-background-secondary" />
                          <div className="space-y-2 p-4">
                            <div className="h-4 w-3/4 animate-pulse rounded bg-background-secondary" />
                            <div className="h-3 w-1/2 animate-pulse rounded bg-background-secondary" />
                          </div>
                        </div>
                      )
                    }
                    const product = item as Product
                    const href = product._id ? `/produits/${product._id}` : "/boutique"
                    const src = product.images?.[0] || "/placeholder.svg"
                    return (
                      <Link
                        key={productKey(product)}
                        href={href}
                        className="group overflow-hidden rounded-2xl border border-border bg-background shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
                      >
                        <div className="relative h-52 w-full overflow-hidden bg-background-secondary">
                          <img
                            src={src}
                            alt={product.name}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                          />
                          <div className="absolute left-3 top-3 rounded-full bg-primary px-2.5 py-1 text-[10px] font-bold text-white shadow-sm">Populaire</div>
                          <div className="absolute bottom-0 left-0 right-0 p-3" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.65), transparent)" }}>
                            <p className="line-clamp-1 text-[11px] text-white/70">{product.category}</p>
                            <p className="mt-0.5 line-clamp-1 text-sm font-bold text-white">{product.name}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between p-4">
                          <p className="text-base font-extrabold text-primary">{formatPrice(product.price)}</p>
                          <div className="flex items-center gap-0.5 text-warning">
                            {[1, 2, 3, 4].map((i) => <Star key={i} size={11} className="fill-warning" />)}
                            <Star size={11} className="fill-warning/30" />
                          </div>
                        </div>
                      </Link>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── AVIS CLIENTS ──────────────────────────────────────────── */}
        <section className="bg-background">
          <div className="container py-16">
            <div className="mx-auto max-w-2xl text-center">
              <span className="inline-block rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold text-primary">Témoignages</span>
              <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
                Ce que disent nos clients
              </h2>
              <p className="mt-3 text-foreground-secondary">
                Plus de 1 000 musiciens nous font confiance à Ouagadougou et dans toute la région.
              </p>
            </div>

            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {TESTIMONIALS.map((t) => (
                <div
                  key={t.name}
                  className="relative flex flex-col gap-4 rounded-2xl border border-border bg-background p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <Quote size={28} className="text-primary/15" />
                  <p className="text-sm leading-relaxed text-foreground-secondary">{t.text}</p>
                  <div className="mt-auto">
                    <div className="flex items-center gap-0.5 text-warning">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} size={12} className={i < t.rating ? "fill-warning" : "fill-warning/25"} />
                      ))}
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                      <div className={`flex h-9 w-9 items-center justify-center rounded-full ${t.color} text-xs font-bold text-white`}>
                        {t.avatar}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground">{t.name}</p>
                        <p className="text-xs text-foreground-secondary">{t.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── GALLERY ───────────────────────────────────────────────── */}
        <section className="bg-background-secondary">
          <div className="container py-16">
            <div className="grid gap-12 lg:grid-cols-12">
              <div className="flex flex-col justify-center lg:col-span-4">
                <span className="inline-block w-fit rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold text-primary">Notre univers</span>
                <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground">
                  Découvrez
                  <br />
                  notre boutique
                </h2>
                <p className="mt-3 text-foreground-secondary">
                  Arrivages, sélections et coulisses — un aperçu de ce qui nous anime.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    href="/a-propos"
                    className="group inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-white transition-all hover:bg-primary/90 hover:shadow-lg"
                  >
                    Notre histoire
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                  <Link
                    href="/contact"
                    className="inline-flex items-center rounded-full border border-border px-6 py-3 text-sm font-bold text-foreground transition hover:bg-background"
                  >
                    Contact
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-8">
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-12 overflow-hidden rounded-2xl">
                    <img
                      src={galleryImages[0] || "/placeholder.svg"}
                      alt="Galerie - notre boutique"
                      className="h-60 w-full object-cover transition-transform duration-700 hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="col-span-6 overflow-hidden rounded-2xl">
                      <img
                        src={galleryImages[i] || "/placeholder.svg"}
                        alt="Galerie - notre univers"
                        className="h-36 w-full object-cover transition-transform duration-700 hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── CONTACT ───────────────────────────────────────────────── */}
        <section className="bg-background">
          <div className="container py-16">
            <div className="grid gap-10 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <span className="inline-block rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold text-primary">Contact</span>
                <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground">Parlons musique</h2>
                <p className="mt-3 text-foreground-secondary">
                  Conseil, devis ou recommandation — notre équipe est à votre écoute.
                </p>

                <div className="mt-8 space-y-4">
                  {contactInfo.map((info) => {
                    const Icon = info.icon
                    return (
                      <div
                        key={info.title}
                        className="flex items-start gap-4 rounded-2xl border border-border bg-background p-5 transition-all hover:-translate-y-0.5 hover:shadow-md"
                      >
                        <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl ${info.color} text-white shadow`}>
                          <Icon size={20} />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-foreground-secondary">{info.title}</p>
                          <p className="mt-0.5 font-bold text-foreground">{info.value}</p>
                          <p className="mt-0.5 text-xs text-foreground-secondary">{info.description}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="lg:col-span-7">
                <h3 className="text-xl font-bold text-foreground">Localisation</h3>
                <p className="mt-2 text-sm text-foreground-secondary">
                  Samandin, Ouagadougou, Burkina Faso — venez découvrir nos instruments en boutique.
                </p>
                <div className="mt-5 overflow-hidden rounded-2xl border border-border shadow-sm">
                  <iframe
                    width="100%"
                    height="400"
                    style={{ border: 0, display: "block" }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src="https://www.google.com/maps/embed?pb=!1m10!1m8!1m3!1d1518.004770412755!2d-1.5680806570075847!3d12.37678645501554!3m2!1i1024!2i768!4f13.1!5e1!3m2!1sfr!2snl!4v1763382716272!5m2!1sfr!2snl"
                    title="Carte - localisation de la boutique"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  )
}
